import { useState, useEffect, Fragment, useCallback, useRef } from "react";
import moment from "moment";
import { useHistory, useLocation } from "react-router-dom";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { AgGridColumn, AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Form, Col, Alert } from "react-bootstrap";
import AddButton from "../../../components/AddButton";
import Button from "../../../components/Button";
import GenericButton from "../../../components/FlowGenericButton";
import Fieldset from "../../../components/Fieldset";
import Modal from "../../../components/Modal";
import ReasonModal from "../../../components/ReasonModal";
import ConfirmationModal from "../../../components/ConfirmationModal";
import If from "../../../components/If";
import AlertError from "../../../components/AlertError";
import AlertSuccess from "../../../components/AlertSuccess";
import DeleteButton from "../../../components/DeleteButton";
import FileExistent from "../../../components/FileExistent";
import { STRINGS } from "../../../util/strings";
import {
  CultureResults,
  ModelsFiles,
  CultivaresRegistroResponse,
  Regions,
  ExtensaoUsoResponse,
  RecResponsavelTecnicoExtUsoAtivo,
} from "../../../models/models";
import api from "../../../services/api";
import Loading from "../../../components/Loading";
import { isDescriptors } from "../../../users/isDescriptors";
import { isDiseases } from "../../../users/isDiseases";
import { isAdmin } from "../../../users/isAdmin";
import ptBR from "date-fns/locale/pt-BR";
import DatePicker, { registerLocale } from "react-datepicker";
import { STATUS_REGISTER } from "../../../util/status";
import { STATUS_EXTENSAO_USO } from "../../../util/statusExtensaoUso";
import { isRequesterWriteRegister, isRequesterReadRegister, isTecnicoExtensaoUso, isOnlyTecnicoExtensaoUso } from "../../../users/isRequester";
import { isReguladorRegister } from "../../../users/isRegulador";
import jwtDecode from "jwt-decode";
import { DecodedToken } from "../../../models/user";
import "../styles_edit.css";
import { _generateDate } from "../../../util/convert";

import mapaImg from "../../../assets/images/recs-mapa.png";


registerLocale("pt-BR", ptBR);

/** Nome do input file fora do RHF — `register` no mesmo índice do useFieldArray corrompe o objeto (RHF v6). */
function extensaoUsoArquivoRecInputName(index: number): string {
  return `extensaoUso-arquivo-rec-${index}`;
}

export default function RegisterRNC() {
  const [dataCultivaresRegistro, setDataCultivaresRegistro] =
    useState<CultivaresRegistroResponse>();

  const [extensaoUso, setExtensaoUso] =
    useState<ExtensaoUsoResponse>();

  const history = useHistory();
  const location = useLocation<{ 
    id: number; 
    codigo?: string; 
    codigoRegistro?: string; 
    novaExtensao?: boolean 
  }>();
  const token = String(localStorage.getItem("token"));
  const { roles }: DecodedToken = jwtDecode(token);
  const loggedUserEmail: string = (() => {
    try {
      const decoded: any = jwtDecode(token);
      return decoded.email?.toLowerCase() || decoded.unique_name?.toLowerCase() || '';
    } catch { return ''; }
  })();
  const [successfulResponse, setSuccessfulResponse] = useState<boolean>(false);

  const getRegistroId = (): number | undefined => {
    const urlParams = new URLSearchParams(window.location.search);
    const idFromUrl = urlParams.get('id');
    if (idFromUrl) {
      return parseInt(idFromUrl, 10);
    }
    return location.state?.id;
  };
  
  const getIsNovaExtensao = (): boolean => {
    const urlParams = new URLSearchParams(window.location.search);
    const novaExtensaoFromUrl = urlParams.get('novaExtensao');
    if (novaExtensaoFromUrl === 'true') {
      return true;
    }
    return location.state?.novaExtensao === true;
  };
  
  const registroId = getRegistroId();
  
  // Código da extensão (CODIGO-ANO ou CODIGO-ANO-N) quando editando extensão específica
  const codigoExtensao = location.state?.codigo;
  
  // Código do registro base
  const codigoRegistroBase = location.state?.codigoRegistro;
  
  // Flag para indicar se está criando uma nova extensão (quando a anterior foi aprovada)
  const isNovaExtensao = getIsNovaExtensao();
  
  // Estado para armazenar informações sobre RECs disponíveis
  const [recsInfo, setRecsInfo] = useState<{
    recsDisponiveis: Array<{ id: number; descricao: string }>;
    recsRegistroOriginal: Array<{ id: number; descricao: string }>;
    recsExtensoesAnteriores: Array<{ id: number; descricao: string; extensaoUsoId: number; anoAprovacao?: number; statusExtensaoUso: string }>;
    recsExtensaoAtual: Array<{ id: number; descricao: string }>;
    mensagem: string;
  } | null>(null);

  const [mapaResponsaveisPorRecId, setMapaResponsaveisPorRecId] = useState<
    Record<string, { nome: string; email: string }>
  >({});
  const [recsSemVinculo, setRecsSemVinculo] = useState<string[]>([]);

  const [chooseAdaptationRegions, setChooseAdaptationRegions] =
    useState<boolean>(false);
  const [unselectedAdaptationRegions, setUnselectedAdaptationRegions] =
    useState<Array<Regions>>([]);
  const [choosenAdaptationRegions, setChoosenAdaptationRegions] = useState<Array<Regions>
  >([]);
  const [selectedAdaptationRegions, setSelectedAdaptationRegions] = useState<
    Array<Regions>
  >([]);
  const [insertAdaptationRegions, setInsertAdaptationRegions] = useState<boolean>(false);
  const [registroAdaptationRegions, setRegistroAdaptationRegions] = useState<
    Array<Regions>
  >([]);

  const [submited, setSubmited] = useState<boolean>(false);
  const [errorOnSubmit, setErrorOnSubmit] = useState<any>(null);
  const [loadingSubmit, setLoadingSubmit] = useState<boolean>(false);

  const [regions, setRegions] = useState<Regions[]>([]);

  const [loadingDraft, setLoadingDraft] = useState<boolean>(false);
  const [successDraft, setSuccessDraft] = useState<boolean>(false);
  const [errorDraft, setErrorDraft] = useState<any>(null);
  const [errorOnForm, setErrorOnForm] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Estados para modal de reprovação
  const [reproveModal, setReproveModal] = useState<boolean>(false);
  const [reproveTxt, setReproveTxt] = useState<string>("");
  const [loadingReprove, setLoadingReprove] = useState<boolean>(false);
  const [successReprove, setSuccessReprove] = useState<boolean>(false);
  const [reproveType, setReproveType] = useState<"regulatorio" | "mapa">("regulatorio");

  // Estado para rastrear arquivos removidos por índice de responsável
  const [arquivosRemovidos, setArquivosRemovidos] = useState<Set<number>>(new Set());

  /** Rótulo do arquivo novo (input não usa register — não entra em getValues). */
  const [arquivoRecPendenteNome, setArquivoRecPendenteNome] = useState<
    Record<number, string>
  >({});

  const [recAprovacoes, setRecAprovacoes] = useState<Record<number, { statusRec: string; motivoReprovacaoRec: string }>>({});

  // Estado para modal de confirmação de envio ao regulatório
  const [regulatoryModal, setRegulatoryModal] = useState<boolean>(false);
  
  // Estado para modal de confirmação de aprovação
  const [aproveModal, setAproveModal] = useState<boolean>(false);
  
  // Estado para modal de aprovação pelo MAPA (com protocolo)
  const [aproveMapaModal, setAproveMapaModal] = useState<boolean>(false);
  const [protocolNumberTemp, setProtocolNumberTemp] = useState<string>("");
  const [protocolDateTemp, setProtocolDateTemp] = useState<Date | null>(null);

  // Estado para modal de confirmação de envio ao técnico
  const [enviarTecnicoModal, setEnviarTecnicoModal] = useState<boolean>(false);

  const [cultureDetails, setCultureDetails] = useState<CultureResults>();
  const speciesRef = useRef({ speciesId: "", speciesName: "", scientificName: "" });
  const [loadingRequestData, setLoadingRequestData] = useState<boolean>(true);

  const [loadingDownload, setLoadingDownload] = useState<boolean>(true);

  const TIPO_REGISTRO_VALUES = ["Rascunho", "Finalizado"] as const;
  type TipoRegistro = (typeof TIPO_REGISTRO_VALUES)[number];

    const handleDownload = (fileUrl: string, modelFileName: string) => {
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = modelFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

  const handleDownloadModeloRec = async () => {
    try {
      const response = await api.get(
        `api/v1/cultivares/ExtensaoUso/DownloadModeloRec`,
        { responseType: 'blob' }
      );

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'modelo_rec.docx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Erro ao baixar modelo:', error);
      setErrorOnForm(true);
      setErrorMessage("Erro ao baixar o modelo. Tente novamente mais tarde.");
    }
  };

  const registerExUsoSchema = yup.object({
    codigo: yup.string().required(STRINGS.FORM_FIELD_REQUIRED),
    status: yup.string().required(STRINGS.FORM_FIELD_REQUIRED),
    speciesId: yup
      .string()
      .transform((v) => (v === undefined || v === null ? "" : String(v)))
      .required(STRINGS.FORM_FIELD_REQUIRED),
    speciesName: yup
      .string()
      .transform((v) => (v === undefined || v === null ? "" : String(v)).trim())
      .required(STRINGS.FORM_FIELD_REQUIRED),
    scientificName: yup
      .string()
      .transform((v) => (v === undefined || v === null ? "" : String(v)).trim())
      .required(STRINGS.FORM_FIELD_REQUIRED),
    protocolNumber: yup.string().nullable().optional(),
    protocolDate: yup.date().nullable().optional(),
    motivoReprovacao: yup.string().nullable().optional(),
    tipoRegistro: yup
      .mixed<TipoRegistro>()
      .oneOf(
        [...TIPO_REGISTRO_VALUES],
        'Tipo de registro deve ser "rascunho" ou "finalizado"'
      )
      .required(STRINGS.FORM_FIELD_REQUIRED)
      .default(TIPO_REGISTRO_VALUES[0]),
    responsavelRecExtUso: yup
      .array()
      .of(
        yup.object({
          nomeResponsavel: yup
            .string()
            .transform((v) => (v == null ? "" : String(v)).trim())
            .required(STRINGS.FORM_FIELD_REQUIRED),
          emailResponsavel: yup
            .string()
            .transform((v) => (v == null ? "" : String(v)).trim())
            .required(STRINGS.FORM_FIELD_REQUIRED)
            .test(
              "emailFmt",
              "E-mail inválido",
              (v) => typeof v === "string" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)
            ),
          idRec: yup
            .mixed()
            .test(
              "idRec",
              STRINGS.FORM_FIELD_REQUIRED,
              (v) => v !== undefined && v !== null && String(v).trim() !== ""
            ),
          descricaoUF: yup.string().nullable().optional(),
          idArqRec: yup
            .mixed()
            .transform((v) =>
              v === undefined || v === null ? "" : String(v)
            )
            .optional(),
          caminhoArqRec: yup
            .string()
            .transform((v) => (v == null ? "" : String(v)))
            .optional(),
          arquivoRec: yup.mixed().nullable().notRequired(),
          statusRec: yup.string().nullable().optional(),
          motivoReprovacaoRec: yup.string().nullable().optional(),
          observacao: yup.string().nullable().optional(),
        })
      )
      .min(1, "É necessário informar pelo menos um responsável")
      .required(STRINGS.FORM_FIELD_REQUIRED),
  });

  type RegisterExUsoSchemaProps = yup.Asserts<typeof registerExUsoSchema>;

  const defaultForm = useForm<RegisterExUsoSchemaProps>({
    mode: "onSubmit",
    resolver: yupResolver(registerExUsoSchema),
    defaultValues: {
      codigo: "",
      status: "",
      speciesId: "",
      speciesName: "",
      scientificName: "",
      protocolNumber: null,
      protocolDate: null,
      motivoReprovacao: null,
      tipoRegistro: TIPO_REGISTRO_VALUES[0],
      responsavelRecExtUso: [],
    },
  });

  const { fields } = useFieldArray({
    control: defaultForm.control,
    name: "responsavelRecExtUso"
  });

  /** Repõe idRec/descricaoUF a partir das regiões (backup; o principal era o register do arquivo). */
  const syncResponsavelRecIdsComRegioes = useCallback(() => {
    const regions = selectedAdaptationRegions ?? [];
    if (!regions.length) return;
    regions.forEach((region, idx) => {
      const idRec = String(region.id ?? "");
      const descricaoUF = region.descricao ?? "";
      const rows = (defaultForm.getValues("responsavelRecExtUso") as any[]) || [];
      const row = rows[idx] ?? {};
      if (String(row.idRec ?? "").trim() !== idRec) {
        (defaultForm as any).setValue(
          `responsavelRecExtUso[${idx}].idRec`,
          idRec,
          false
        );
      }
      if (String(row.descricaoUF ?? "").trim() !== String(descricaoUF).trim()) {
        (defaultForm as any).setValue(
          `responsavelRecExtUso[${idx}].descricaoUF`,
          descricaoUF,
          false
        );
      }
    });
  }, [selectedAdaptationRegions, defaultForm]);

  const downloadFile = async (nomeArquivo: string, pathArquivo: string) => {
    if (nomeArquivo && pathArquivo) {
      setLoadingDownload(true);
      try {
        const response = await api.get(
          `api/v1/cultivares/ExtensaoUso/Download`,
          {
            params: { path: pathArquivo, fileName: nomeArquivo },
            responseType: 'blob',
          }
        );

        // Cria um Blob com o conteúdo retornado
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', nomeArquivo);
        document.body.appendChild(link);
        link.click();

        // Libera o objeto após o download
        link.remove();
        window.URL.revokeObjectURL(url);

      } catch (error: any) {
        console.error('Erro ao baixar arquivo:', error);
      } finally {
        setLoadingDownload(false);
      }
    }
  };

  useEffect(() => {
    const loadMapaResponsaveis = async () => {
      try {
        const { data } = await api.get<RecResponsavelTecnicoExtUsoAtivo[]>(
          "api/v1/cultivares/RecResponsavelTecnicoExtensaoUso/obter-todos-ativos"
        );
        const mapa: Record<string, { nome: string; email: string }> = {};
        (data ?? []).forEach((item) => {
          mapa[String(item.culturaRegiaoId)] = {
            nome: item.nome ?? "",
            email: item.email ?? "",
          };
        });
        setMapaResponsaveisPorRecId(mapa);
      } catch (err) {
        console.error("Erro ao carregar vínculos REC x técnico:", err);
      }
    };
    loadMapaResponsaveis();
  }, []);

  useEffect(() => {
    const currentValues = defaultForm.getValues("responsavelRecExtUso") ?? [];

    const semVinculo: string[] = [];
    const updatedValues = !selectedAdaptationRegions?.length
      ? []
      : selectedAdaptationRegions.map((region) => {
          const existing =
            currentValues.find(
              (i: any) =>
                String(i.idRec) === String(region.id) &&
                String(i.descricaoUF ?? "").trim() ===
                  String(region.descricao ?? "").trim()
            ) ??
            currentValues.find(
              (i: any) => String(i.idRec) === String(region.id)
            );

          const vinculo = mapaResponsaveisPorRecId[String(region.id)];
          const descricaoRec = region.descricao ?? String(region.id);
          let nomeResponsavel = existing?.nomeResponsavel || "";
          let emailResponsavel = existing?.emailResponsavel || "";

          if (vinculo) {
            nomeResponsavel = vinculo.nome;
            emailResponsavel = vinculo.email;
          } else if (!nomeResponsavel && !emailResponsavel) {
            semVinculo.push(descricaoRec);
          }

          return {
            nomeResponsavel,
            emailResponsavel,
            idRec: String(region.id ?? ""),
            descricaoUF: region.descricao ?? "",
            idArqRec: existing?.idArqRec || "",
            caminhoArqRec: existing?.caminhoArqRec || "",
            arquivoRec: existing?.arquivoRec ?? null,
            statusRec: existing?.statusRec ?? "",
            motivoReprovacaoRec: existing?.motivoReprovacaoRec ?? "",
            observacao: existing?.observacao ?? "",
          };
        });
    setRecsSemVinculo(semVinculo);

    // Um único `reset` alinha o useFieldArray com a lista de RECs (vários `append` seguidos no v6
    // deixava `fields` / valores dessincronizados — ex.: 4 RECs na grade e só 3 linhas em Responsáveis).
    // speciesRef garante que speciesId/Name/scientificName não sejam perdidos quando ParseGroup01
    // (componente inline) desmonta/remonta e os hidden inputs perdem seu valor no RHF v6.
    const prev = defaultForm.getValues();
    const { speciesId, speciesName, scientificName } = speciesRef.current;
    defaultForm.reset({
      ...prev,
      ...(speciesId ? { speciesId, speciesName, scientificName } : {}),
      responsavelRecExtUso: updatedValues,
    });
    setArquivoRecPendenteNome({});
    if (typeof document !== "undefined") {
      document
        .querySelectorAll<HTMLInputElement>(
          'input[type="file"][name^="extensaoUso-arquivo-rec-"]'
        )
        .forEach((el) => {
          el.value = "";
        });
    }
  }, [selectedAdaptationRegions, mapaResponsaveisPorRecId]);

  useEffect(() => {
    if (!cultureDetails?.id) return;
    const sid = String(cultureDetails.id);
    const sname = cultureDetails.nome ?? "";
    const sciname = cultureDetails.descricao ?? "";
    speciesRef.current = { speciesId: sid, speciesName: sname, scientificName: sciname };
    defaultForm.setValue("speciesId", sid);
    defaultForm.setValue("speciesName", sname);
    defaultForm.setValue("scientificName", sciname);
  }, [cultureDetails]);

  const getTipoRegistroLabel = (tipo: number) : string => {
    return TIPO_REGISTRO_VALUES[tipo as number] as string;
  };

  useEffect(() => {
    const getDataNeeded = async () => {
      getRequest();
    };

    const getRequest = async () => {

      let codigoByData = undefined;
      let getCultureByIdData: CultureResults | undefined = undefined 

      try {
        const { data } = await api.get<CultivaresRegistroResponse>(
          `api/v1/cultivares/CultivaresRegistro/${registroId}`
        );
        setDataCultivaresRegistro(data);

        codigoByData = data.codigo;
        defaultForm.setValue("codigo", data?.codigo);
        // Inicializa o status como NaoIniciado (será sobrescrito se existir ExtensaoUso)
        defaultForm.setValue("status", STATUS_EXTENSAO_USO.NaoIniciado);

        // Carrega as regiões de adaptação do registro original (Zoneamento Atual)
        if (data.cultivaresCulturaRegioes && data.cultivaresCulturaRegioes.length > 0) {
          const formattedRegistroRegions = data.cultivaresCulturaRegioes.map(
            (reg: any) => ({
              id: reg.culturaRegiao?.id || reg.culturaRegiaoId,
              descricao: reg.culturaRegiao?.descricao || reg.descricao,
            })
          );
          setRegistroAdaptationRegions(formattedRegistroRegions);
        }

        getCultureByIdData = await getCultureById(data.culturaId);
      
        setInsertAdaptationRegions(true);

        // Busca informações sobre RECs disponíveis
        try {
          const { data: recsData } = await api.get(
            `api/v1/cultivares/ExtensaoUso/recs-disponiveis/${registroId}`
          );
          setRecsInfo(recsData);
        } catch (recsError) {
          console.error("Erro ao buscar RECs disponíveis:", recsError);
        }

      } catch (error: any) {
        console.error(error);
      }

      try {
        if (!isNovaExtensao) {
          // Usa o código da extensão específica se foi fornecido, senão busca por código do registro
          const codigoBusca = codigoExtensao || codigoByData;
          
          if (codigoBusca) {
            let extensaoUsoData: ExtensaoUsoResponse | null = null;
            
            // Se é código de extensão específica (formato: CODIGO-ANO ou CODIGO-ANO-N), busca diretamente
            if (codigoExtensao && codigoExtensao !== codigoByData) {
              const { data: dataExt } = await api.get<ExtensaoUsoResponse>(
                `api/v1/cultivares/ExtensaoUso/codigo/${codigoExtensao}`
              ).catch(() => ({ data: null }));
              extensaoUsoData = dataExt;
            } else {
              // Senão, busca a extensão ativa ou a mais recente pelo código do registro
              const { data: dataExtensaoUso } = await api.get<ExtensaoUsoResponse>(
                `api/v1/cultivares/ExtensaoUso/ativa/${codigoByData}`
              ).catch(() => ({ data: null }));
              
              extensaoUsoData = dataExtensaoUso || (await api.get<ExtensaoUsoResponse>(
                `api/v1/cultivares/ExtensaoUso/codigo/${codigoByData}`
              ).catch(() => ({ data: null }))).data;
            }

            if (extensaoUsoData) {
              setExtensaoUso(extensaoUsoData);

              if (extensaoUsoData?.responsavelRecExtUso?.length > 0) {
                const formattedRegions = extensaoUsoData?.responsavelRecExtUso.map(
                  (recExtUso) => ({
                    id: Number(recExtUso?.idRec),
                    descricao: recExtUso?.descricaoUF,
                  })
                );
        
                setSelectedAdaptationRegions(formattedRegions);
                setInsertAdaptationRegions(true);
              }

              extensaoUsoData.tipoRegistro = getTipoRegistroLabel(
                extensaoUsoData?.tipoRegistro as number
              );
              
              // Só converte protocolDate se for uma data válida
              if (extensaoUsoData.protocolDate && moment(extensaoUsoData.protocolDate).isValid()) {
                extensaoUsoData.protocolDate = moment(extensaoUsoData.protocolDate).toDate();
              } else {
                extensaoUsoData.protocolDate = null;
              }

              // Prepara os responsáveis no formato correto antes do reset
              if (extensaoUsoData?.responsavelRecExtUso?.length > 0) {
                extensaoUsoData.responsavelRecExtUso = extensaoUsoData.responsavelRecExtUso.map((resp: any) => ({
                  idRec: resp.idRec,
                  descricaoUF: resp.descricaoUF,
                  nomeResponsavel: resp.nomeResponsavel || "",
                  emailResponsavel: resp.emailResponsavel || "",
                  arquivoRec: resp.arquivoRec || null,
                  caminhoArqRec: resp.arquivoRec?.caminhoArqRec || "",
                  idArqRec: resp.arquivoRec?.id?.toString() || "",
                  statusRec: resp.statusRec || null,
                  motivoReprovacaoRec: resp.motivoReprovacaoRec || "",
                  observacao: resp.observacao || "",
                }));

                // Inicializa o estado de aprovação por REC a partir dos dados da API
                const statusExt = extensaoUsoData.status as string | undefined;
                const emAnaliseRegulatorio =
                  statusExt === STATUS_EXTENSAO_USO.AguardandoRegulatorio ||
                  statusExt === "AguardandoRegulatorio";
                const defaultAprovadoSohRegulatorio =
                  (isReguladorRegister(roles) || isAdmin(roles)) &&
                  emAnaliseRegulatorio;

                const aprovInicial: Record<number, { statusRec: string; motivoReprovacaoRec: string }> = {};
                extensaoUsoData.responsavelRecExtUso.forEach((resp: any, idx: number) => {
                  const semDecisaoApi = resp.statusRec == null || resp.statusRec === "";
                  let statusRecIni = resp.statusRec ?? "";
                  if (semDecisaoApi && defaultAprovadoSohRegulatorio) {
                    statusRecIni = "Aprovado";
                  }
                  aprovInicial[idx] = {
                    statusRec: statusRecIni,
                    motivoReprovacaoRec: resp.motivoReprovacaoRec ?? "",
                  };
                });
                setRecAprovacoes(aprovInicial);
              }
              
              defaultForm.reset(extensaoUsoData);
            }
          }
        } else if (isNovaExtensao) {
          // Se é nova extensão, inicializa o formulário vazio com status NaoIniciado
          defaultForm.setValue("status", STATUS_EXTENSAO_USO.NaoIniciado);
          // Limpa as regiões selecionadas para a nova extensão
          setSelectedAdaptationRegions([]);
        }
      } catch (error: any) {
        console.error(error);
      }

      setTimeout(() => {
        setLoadingRequestData(false);
      }, 2500);
    };

    getDataNeeded();
  }, [registroId, codigoExtensao, defaultForm.reset, isNovaExtensao]);

  const onRowSelectedAdaptationRegions = ({ api }: any) => {
    const data = api.getSelectedRows();
    setChoosenAdaptationRegions(data);
  };

  const setUnselectedAdaptationRegion = (regionsSelected: Regions[]) => {
    // Se temos informações de RECs disponíveis do backend, usa elas
    if (recsInfo?.recsDisponiveis) {
      // Filtra apenas as RECs que estão disponíveis e não foram selecionadas
      const results = recsInfo.recsDisponiveis.filter(rec => 
        !regionsSelected.some(selected => selected.id === rec.id)
      );
      setUnselectedAdaptationRegions(results);
      return;
    }

    // Fallback: filtra manualmente se não tiver info do backend
    let results: Regions[] = [];

    // Cria set de IDs de regiões ocupadas por extensões anteriores
    const recsExtensoesAnteriores = new Set(
      recsInfo?.recsExtensoesAnteriores?.map(r => r.id) || []
    );

    for (let count = 0, countLen = regions.length; count < countLen; count++) {
      // Exclui regiões já selecionadas para extensão de uso atual
      if (regionsSelected.some((c) => c.id === regions[count].id)) continue;
      
      // Exclui regiões que já estão no registro original (Zoneamento Atual)
      if (registroAdaptationRegions.some((c) => c.id === regions[count].id)) continue;

      // Exclui regiões que já foram usadas em extensões anteriores aprovadas
      if (regions[count].id && recsExtensoesAnteriores.has(regions[count].id as number)) continue;

      results.push(regions[count]);
    }

    setUnselectedAdaptationRegions(results);
  };

  const carregarDadosArquivos = (values: any, arquivosEnviados: Array<any>) => {
    if (!values?.responsavelRecExtUso) return values;

    values.responsavelRecExtUso = values.responsavelRecExtUso.map((item: any) => {
      const { idRec } = item;
           
      const responsavelByRequest = extensaoUso?.responsavelRecExtUso?.find((r) => r.idRec === idRec);

      const idArquivoRec = responsavelByRequest?.arquivoRec?.id;
      const arquivoServidor = arquivosEnviados?.find((arq: any) => +arq.idRec === +idRec);
      const caminhoArqRecSalvar = arquivoServidor?.caminhoArqRec ? arquivoServidor.caminhoArqRec : responsavelByRequest?.arquivoRec?.caminhoArqRec;
      const nomeSalvar = arquivoServidor?.name ? arquivoServidor.name : responsavelByRequest?.arquivoRec?.name;

      if (arquivoServidor?.caminhoArqRec || responsavelByRequest?.arquivoRec) {
        item.arquivoRec = {
          id: idArquivoRec,
          idRec: Number(idRec),
          caminhoArqRec: caminhoArqRecSalvar,
          name: nomeSalvar,
        };
      }

      return item;
    });

    return values;
  };

  const extrairPrimeiraMensagemErro = (node: any): string | null => {
    if (!node) return null;
    if (typeof node.message === "string" && node.message.trim()) {
      return node.message.trim();
    }
    if (Array.isArray(node)) {
      for (const item of node) {
        const m = extrairPrimeiraMensagemErro(item);
        if (m) return m;
      }
      return null;
    }
    if (typeof node === "object") {
      for (const k of Object.keys(node)) {
        const m = extrairPrimeiraMensagemErro((node as any)[k]);
        if (m) return m;
      }
    }
    return null;
  };

  const onError = (errors: typeof defaultForm.formState.errors) => {
    setErrorOnForm(true);

    const valores = defaultForm.getValues();
    try {
      console.groupCollapsed("[ExtensaoUso/Edit] validação falhou — onError");
      console.log("errors (react-hook-form):", errors);
      console.log("getValues() completo:", valores);
      console.log("responsavelRecExtUso:", valores.responsavelRecExtUso);
      console.log("selectedAdaptationRegions:", selectedAdaptationRegions);
      console.log(
        "useFieldArray fields:",
        fields?.map((f: any, i: number) => ({
          i,
          fieldId: f.id,
          idRec: f.idRec,
          descricaoUF: f.descricaoUF,
        }))
      );
      if (Array.isArray(errors.responsavelRecExtUso)) {
        errors.responsavelRecExtUso.forEach((itemErr: any, idx: number) => {
          if (!itemErr || typeof itemErr !== "object") return;
          Object.keys(itemErr).forEach((campo) => {
            const e = itemErr[campo];
            if (e && typeof e.message === "string") {
              console.log(
                `[responsavelRecExtUso[${idx}].${campo}]`,
                e.message,
                "| type:",
                e.type,
                "| valor no form:",
                valores.responsavelRecExtUso?.[idx]
              );
            }
          });
        });
      } else if (errors.responsavelRecExtUso) {
        console.log(
          "errors.responsavelRecExtUso (não é array):",
          errors.responsavelRecExtUso
        );
      }
      (
        [
          "codigo",
          "status",
          "speciesId",
          "speciesName",
          "scientificName",
          "tipoRegistro",
        ] as const
      ).forEach((k) => {
        const e = (errors as any)[k];
        if (e?.message) {
          console.log(`[${k}]`, e.message, "| valor:", (valores as any)[k]);
        }
      });
      console.groupEnd();
    } catch (e) {
      console.log("[ExtensaoUso/Edit] falha ao logar onError", e);
    }
    
    // Verifica erros específicos e define mensagem apropriada
    if (errors.responsavelRecExtUso) {
      if (Array.isArray(errors.responsavelRecExtUso)) {
        const detalhe = extrairPrimeiraMensagemErro(errors.responsavelRecExtUso);
        setErrorMessage(
          detalhe ||
            "Preencha todos os campos obrigatórios da seção '3. Extensão de Uso'."
        );
      } else {
        const detalhe = extrairPrimeiraMensagemErro(errors.responsavelRecExtUso);
        setErrorMessage(
          detalhe ||
            "É obrigatório preencher a seção '3. Extensão de Uso' antes de enviar."
        );
      }
    } else {
      const detalhe = extrairPrimeiraMensagemErro(errors);
      setErrorMessage(detalhe || STRINGS.ALERT_WARNING_FIELDS);
    }
  };

  const onSubmit = async (values: RegisterExUsoSchemaProps) => {
    // Prioridade para determinar o código correto:
    // 1. Se está criando nova extensão (isNovaExtensao=true), usa o código do registro
    // 2. Se tem extensaoUso carregada, usa o código dela
    // 3. Se veio da listagem com código específico, usa ele
    // 4. Por último, usa o código do formulário
    let codigoParaEnviar = values.codigo;
    let idParaEnviar: number | null = null;
    
    if (isNovaExtensao) {
      // Criando nova extensão - usa código do registro, backend vai gerar -ANO
      codigoParaEnviar = dataCultivaresRegistro?.codigo || values.codigo;
      idParaEnviar = null;
    } else if (extensaoUso?.codigo) {
      // Editando extensão existente - usa código e id da extensão
      codigoParaEnviar = extensaoUso.codigo;
      idParaEnviar = extensaoUso.id || null;
    } else if (codigoExtensao && codigoExtensao !== dataCultivaresRegistro?.codigo) {
      // Veio da listagem com código de extensão específica
      codigoParaEnviar = codigoExtensao;
    }
    
    // Limpa erros anteriores
    setErrorOnForm(false);
    setErrorMessage("");
    
    setLoadingSubmit(true);
    setErrorOnSubmit(null);

    // salvar arquivos primeiro    
    let arquivosEnviados = new Array<any>();

    try {
      const formDataFiles = new FormData();

      // Lê os arquivos diretamente do DOM
      fields.forEach((field: any, index: number) => {
        const inputSelector = `input[name="${extensaoUsoArquivoRecInputName(index)}"]`;
        const inputFile = document.querySelector(inputSelector) as HTMLInputElement;

        if (inputFile?.files && inputFile.files.length > 0) {
          const idRecRow =
            field.idRec ??
            (defaultForm.getValues(`responsavelRecExtUso[${index}].idRec` as any) as
              | string
              | undefined);
          formDataFiles.append(`fileArquivoRec-${idRecRow}`, inputFile.files[0]);
        }
      });

      const hasFiles = Array.from(formDataFiles.keys()).length > 0;

      if (hasFiles) {
        const { data: dataArquivos, status } = await api.post(
          `api/v1/cultivares/ExtensaoUso/${dataCultivaresRegistro?.codigo}/SalvarArquivosRegistro`,
          formDataFiles,
          { headers: { "Content-Type": "multipart/form-data" } }
        );

        if (status === 200 && Array.isArray(dataArquivos)) {
          arquivosEnviados = [...dataArquivos];
        }
      }
    } catch (error: any) {
      console.error('[ExtensaoUso] Erro no upload de arquivos:', error);
    }

    try {
      const valuesAjustados = carregarDadosArquivos(values, arquivosEnviados);

      // Só regulador/admin pode enviar decisão por REC; técnico/requisitante deve mandar null
      // (caso contrário o front assumia "Aprovado" e o backend ia para Aguardando MAPA).
      const podeMesclarDecisoesRec =
        isReguladorRegister(roles) || isAdmin(roles);

      // Transforma os dados do arquivo para o formato esperado pelo backend
      if (valuesAjustados.responsavelRecExtUso) {
        valuesAjustados.responsavelRecExtUso = valuesAjustados.responsavelRecExtUso.map((resp: any, index: number) => {
          if (!podeMesclarDecisoesRec) {
            resp.statusRec = null;
            resp.motivoReprovacaoRec = null;
          } else {
            const aprovacao = recAprovacoes[index];
            const sr = aprovacao?.statusRec;
            if (sr === "Aprovado" || sr === "Reprovado") {
              resp.statusRec = sr;
              resp.motivoReprovacaoRec =
                sr === "Reprovado"
                  ? aprovacao?.motivoReprovacaoRec || null
                  : null;
            } else {
              resp.statusRec = null;
              resp.motivoReprovacaoRec = null;
            }
          }

          const { arquivoRec, ...rest } = resp;

          // Se o arquivo foi removido mas há um novo arquivo enviado para esta REC,
          // não deve tratar como removido — o novo arquivo deve prevalecer
          const idRecAtual = resp.idRec;
          const arquivoNovoEnviado = arquivosEnviados?.find((arq: any) => +arq.idRec === +idRecAtual);
          const foiRemovidoSemSubstituto = arquivosRemovidos.has(index) && !arquivoNovoEnviado;

          // Se o arquivo foi removido sem substituto, envia null para limpar no backend
          if (foiRemovidoSemSubstituto) {
            return {
              ...rest,
              arquivoRec: null
            };
          }
          
          // Se tem arquivo (novo ou existente), mantém o objeto arquivoRec
          if (arquivoRec && (arquivoRec.caminhoArqRec || arquivoRec.id)) {
            return {
              ...rest,
              arquivoRec: {
                id: arquivoRec.id || null,
                idRec: Number(resp.idRec) || arquivoRec.idRec,
                caminhoArqRec: arquivoRec.caminhoArqRec,
                name: arquivoRec.name
              }
            };
          }
          return rest;
        });
      }

      // Garante que o código e id da extensão estão corretos no payload
      valuesAjustados.codigo = codigoParaEnviar;
      if (idParaEnviar) {
        valuesAjustados.id = idParaEnviar;
      }
      
      const request = await api.post(`api/v1/cultivares/ExtensaoUso`, valuesAjustados);
      
      if (request?.status !== 200 && request?.status !== 201) {
        setErrorOnSubmit(request.data);
      } else {
        if (request?.data?.result) {
          const dataSavedExtensaoUso = request.data.result;

          setExtensaoUso(dataSavedExtensaoUso);

          dataSavedExtensaoUso.tipoRegistro = getTipoRegistroLabel(dataSavedExtensaoUso?.tipoRegistro as number);
          if (dataSavedExtensaoUso.protocolDate && moment(dataSavedExtensaoUso.protocolDate).isValid()) {
            dataSavedExtensaoUso.protocolDate = moment(dataSavedExtensaoUso.protocolDate).toDate();
          } else {
            dataSavedExtensaoUso.protocolDate = null;
          }

          defaultForm.reset(dataSavedExtensaoUso);
        }
        // Limpa o estado de arquivos removidos após salvar com sucesso
        setArquivosRemovidos(new Set());
        setSuccessfulResponse(true);
      }

    } catch (error: any) {
      console.error(error);
    }

    history.push({pathname: '/cultivares/extensao-uso/consultar'})

    setLoadingSubmit(false);
  };

  const onSubmitDraft = async () => {
    setLoadingDraft(true);
    setErrorOnForm(false);
    setErrorMessage("");
    setErrorDraft(null);

    try {
      const values = defaultForm.getValues();
      
      // IMPORTANTE: Mesma lógica do onSubmit para determinar código correto
      let codigoParaEnviar = values.codigo;
      let idParaEnviar: number | null = null;
      
      if (isNovaExtensao) {
        codigoParaEnviar = dataCultivaresRegistro?.codigo || values.codigo;
        idParaEnviar = null;
      } else if (extensaoUso?.codigo) {
        codigoParaEnviar = extensaoUso.codigo;
        idParaEnviar = extensaoUso.id || null;
      } else if (codigoExtensao && codigoExtensao !== dataCultivaresRegistro?.codigo) {
        codigoParaEnviar = codigoExtensao;
      }
      
      const statusAtual = getStatusNormalizado();
      if (!values.status || values.status === "") {
        values.status = statusAtual;
      }
      
      let arquivosEnviados = new Array<any>();
      
      try {
        const formDataFiles = new FormData();
        
        values.responsavelRecExtUso?.forEach((resp: any, index: number) => {
          const inputFile = document.querySelector(
            `input[name="${extensaoUsoArquivoRecInputName(index)}"]`
          ) as HTMLInputElement;
          const file = inputFile?.files?.[0];
          if (file && file.size > 0) {
            formDataFiles.append(`fileArquivoRec-${resp.idRec}`, file);
          }
        });
        
        const hasFiles = Array.from(formDataFiles.keys()).length > 0;
        
        if (hasFiles) {
          const { data: dataArquivos, status } = await api.post(
            `api/v1/cultivares/ExtensaoUso/${dataCultivaresRegistro?.codigo}/SalvarArquivosRegistro`,
            formDataFiles,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
          
          if (status === 200 && Array.isArray(dataArquivos)) {
            arquivosEnviados = [...dataArquivos];
          }
        }
    } catch (error: any) {
        console.error("Erro ao salvar arquivos:", error);
      }
      
      const valuesAjustados = carregarDadosArquivos(values, arquivosEnviados);
      
      if (valuesAjustados.responsavelRecExtUso) {
        valuesAjustados.responsavelRecExtUso = valuesAjustados.responsavelRecExtUso.map((resp: any, index: number) => {
          const { arquivoRec, ...rest } = resp;
          
          if (arquivosRemovidos.has(index)) {
            return { ...rest, arquivoRec: null };
          }
          
          if (arquivoRec && (arquivoRec.caminhoArqRec || arquivoRec.id)) {
            return {
              ...rest,
              arquivoRec: {
                id: arquivoRec.id || null,
                idRec: Number(resp.idRec) || arquivoRec.idRec,
                caminhoArqRec: arquivoRec.caminhoArqRec,
                name: arquivoRec.name
              }
            };
          }
          return rest;
        });
      }
      
      // Garante que o código e id da extensão estão corretos no payload
      valuesAjustados.codigo = codigoParaEnviar;
      if (idParaEnviar) {
        valuesAjustados.id = idParaEnviar;
      }
      
      const request = await api.post(`api/v1/cultivares/ExtensaoUso`, valuesAjustados);
      
      if (request?.status !== 200 && request?.status !== 201) {
        setErrorDraft(request.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        if (request?.data?.result) {
          const dataSavedExtensaoUso = request.data.result;
          setExtensaoUso(dataSavedExtensaoUso);
          
          dataSavedExtensaoUso.tipoRegistro = getTipoRegistroLabel(dataSavedExtensaoUso?.tipoRegistro as number);
          if (dataSavedExtensaoUso.protocolDate && moment(dataSavedExtensaoUso.protocolDate).isValid()) {
            dataSavedExtensaoUso.protocolDate = moment(dataSavedExtensaoUso.protocolDate).toDate();
          } else {
            dataSavedExtensaoUso.protocolDate = null;
          }
          
          defaultForm.reset(dataSavedExtensaoUso);
        }
        setArquivosRemovidos(new Set());
        setSuccessDraft(true);
        
        history.push({ pathname: '/cultivares/extensao-uso/consultar' });
      }
    } catch (error: any) {
      console.error(error);
      setErrorDraft(error?.response?.data || "Erro ao salvar rascunho");
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    
    setLoadingDraft(false);
  };

  const getCultureById = async (idCulture_: any): Promise<CultureResults | undefined> => {
    try {
      let idCulture = Number(idCulture_);

      if (!idCulture || idCulture <= 0) throw new Error("Cultura inválida");

      const { data, status } = await api.get<CultureResults>(
        `api/v1/cultivares/Cultura/${Number(idCulture)}`
      );

      if (status !== 200 && status !== 201)
        throw new Error("Erro ao obter cultura");

      defaultForm.setValue("speciesId", String(data.id));
      defaultForm.setValue("speciesName", data.nome);
      defaultForm.setValue("scientificName", data.descricao);

      setCultureDetails(data);
			setRegions(data.culturaRegioes);

      return data;
    } catch (error: any) {
      console.error(error);
    }
  };

  // Função para normalizar o status (trata valores antigos do banco)
  const getStatusNormalizado = () => {
    // Se está criando uma nova extensão, sempre retorna "Não Iniciado"
    if (isNovaExtensao) {
      return STATUS_EXTENSAO_USO.NaoIniciado;
    }
    
    const statusBruto = extensaoUso?.status;
    
    // Se não tem extensão de uso, está "Não Iniciado"
    if (!extensaoUso || !statusBruto) {
      return STATUS_EXTENSAO_USO.NaoIniciado;
    }
    
    // Se é um dos novos status válidos, retorna
    const statusValidos = Object.values(STATUS_EXTENSAO_USO);
    if (statusValidos.includes(statusBruto as any)) {
      return statusBruto;
    }
    
    // Valores antigos do banco (números como string) - tratar como "Aguardando Responsável"
    return STATUS_EXTENSAO_USO.AguardandoResponsavel;
  };

  // Função para reprovar a extensão de uso
  const handleReprove = async () => {
    try {
      setLoadingReprove(true);
      
      const novoStatus = reproveType === "regulatorio" 
        ? STATUS_EXTENSAO_USO.ReprovadoPeloRegulatorio 
        : STATUS_EXTENSAO_USO.ReprovadoPeloMAPA;
      
      // Atualiza o status no formulário
      defaultForm.setValue("status", novoStatus);
      
      // Pega os valores atuais do formulário
      const values = defaultForm.getValues();
      values.status = novoStatus;
      values.motivoReprovacao = reproveTxt;
      
      let codigoParaEnviar = values.codigo;
      let idParaEnviar: number | null = null;
      
      if (extensaoUso?.codigo) {
        codigoParaEnviar = extensaoUso.codigo;
        idParaEnviar = extensaoUso.id || null;
      } else if (codigoExtensao && codigoExtensao !== dataCultivaresRegistro?.codigo) {
        codigoParaEnviar = codigoExtensao;
      }
      
      const valuesAjustados = { ...values };
      valuesAjustados.codigo = codigoParaEnviar;
      if (idParaEnviar) {
        (valuesAjustados as any).id = idParaEnviar;
      }
      
      // Salva a extensão de uso com o novo status e motivo
      const { data, status } = await api.post(
        `api/v1/cultivares/ExtensaoUso`,
        valuesAjustados
      );
      
      if (status === 200 || status === 201) {
        setSuccessReprove(true);
        setTimeout(() => {
          history.goBack();
          setReproveModal(false);
        }, 2000);
      }
    } catch (error) {
      console.error("Erro ao reprovar:", error);
    } finally {
      setLoadingReprove(false);
    }
  };

  const parseButton = () => {
    const statusExtensao = getStatusNormalizado();
    const userIsAdmin = isAdmin(roles);
    const userIsRegulatorio = isReguladorRegister(roles);
    const userIsRequester = isRequesterWriteRegister(roles);
    const userIsTecnico = isTecnicoExtensaoUso(roles);
    const userIsOnlyTecnico = isOnlyTecnicoExtensaoUso(roles);

    // Botão Voltar (sempre visível)
    let buttonVoltar = (
      <Button
        title="Voltar"
        type="button"
        contained={false}
        onClick={() => history.goBack()}
      />
    );

    // Botão Salvar Rascunho - visível em todas as situações onde o usuário pode preencher dados
    // Salva sem mudar o status (mantém o status atual)
    let buttonSalvarRascunho = <Fragment />;
    
    // Requisitante/Admin pode salvar rascunho em: Não Iniciado, Aguardando Responsável, Reprovado pelo Regulatório
    if (
      (userIsAdmin || userIsRequester) && 
      (statusExtensao === STATUS_EXTENSAO_USO.NaoIniciado || 
       statusExtensao === STATUS_EXTENSAO_USO.AguardandoResponsavel ||
       statusExtensao === STATUS_EXTENSAO_USO.ReprovadoPeloRegulatorio)
    ) {
      buttonSalvarRascunho = (
        <Button
          title="Salvar Rascunho"
          type="button"
          contained={false}
          onClick={() => {
            onSubmitDraft();
          }}
          isLoading={loadingDraft}
        />
      );
    }
    
    // Técnico pode salvar rascunho em: Aguardando Responsável, Reprovado pelo Regulatório
    if (
      userIsTecnico && 
      (statusExtensao === STATUS_EXTENSAO_USO.AguardandoResponsavel ||
       statusExtensao === STATUS_EXTENSAO_USO.ReprovadoPeloRegulatorio)
    ) {
      buttonSalvarRascunho = (
        <Button
          title="Salvar Rascunho"
          type="button"
          contained={false}
          onClick={() => {
            onSubmitDraft();
          }}
          isLoading={loadingDraft}
        />
      );
    }

    // Botão Enviar para Técnico - visível quando status é "Não Iniciado"
    let buttonEnviarResponsavel = <Fragment />;
    if (
      (userIsAdmin || userIsRequester) && 
      statusExtensao === STATUS_EXTENSAO_USO.NaoIniciado
    ) {
      buttonEnviarResponsavel = (
        <Button
          title="Enviar para Técnico"
          type="button"
          contained={true}
          onClick={() => {
            // Valida se há responsáveis preenchidos na seção "3. Extensão de Uso"
            // Usa fields (do useFieldArray) que é a fonte de verdade atualizada
            if (!fields || fields.length === 0) {
              setErrorOnForm(true);
              setErrorMessage("É obrigatório adicionar pelo menos uma localidade na seção '3. Extensão de Uso' antes de enviar para o técnico.");
              window.scrollTo({ top: 0, behavior: 'smooth' });
              return;
            }
            
            setErrorOnForm(false);
            setErrorMessage("");
            setEnviarTecnicoModal(true);
          }}
          isLoading={loadingSubmit}
        />
      );
    }

    // Botão Enviar para Regulatório - visível quando status é "Aguardando Técnico" ou "Reprovado Pelo Regulatório"
    // Técnico também pode enviar para regulatório quando está em "Aguardando Técnico"
    let buttonEnviarRegulatorio = <Fragment />;
    if (
      (userIsAdmin || userIsRequester || userIsTecnico) && 
      (statusExtensao === STATUS_EXTENSAO_USO.AguardandoResponsavel ||
       statusExtensao === STATUS_EXTENSAO_USO.ReprovadoPeloRegulatorio)
    ) {
      const tituloBtn = statusExtensao === STATUS_EXTENSAO_USO.ReprovadoPeloRegulatorio 
        ? "Reenviar para Regulatório" 
        : "Enviar para Regulatório";
      
      buttonEnviarRegulatorio = (
        <Button
          title={tituloBtn}
          type="button"
          contained={true}
          onClick={() => {
            // Valida se há responsáveis preenchidos na seção "3. Extensão de Uso"
            // Usa fields (do useFieldArray) que é a fonte de verdade atualizada
            if (!fields || fields.length === 0) {
              setErrorOnForm(true);
              setErrorMessage("É obrigatório adicionar pelo menos uma localidade na seção '3. Extensão de Uso'.");
              window.scrollTo({ top: 0, behavior: 'smooth' });
              return;
            }
            
            // Verifica se cada responsável tem arquivo (novo ou existente)
            const responsaveisSemArquivo: string[] = [];
            
            fields.forEach((field: any, index: number) => {
              // Verifica diretamente o input file no DOM
              const inputFile = document.querySelector(
                `input[name="${extensaoUsoArquivoRecInputName(index)}"]`
              ) as HTMLInputElement;
              const temArquivoNovo = inputFile?.files && inputFile.files.length > 0;
              
              // Verifica se tem arquivo existente no servidor
              const temArquivoExistente = extensaoUso?.responsavelRecExtUso?.find(
                (r) => String(r.idRec) === String(field.idRec)
              )?.arquivoRec?.caminhoArqRec;
              
              const arquivoFoiRemovido = arquivosRemovidos.has(index);
              
              if (!temArquivoNovo && (!temArquivoExistente || arquivoFoiRemovido)) {
                responsaveisSemArquivo.push(field.descricaoUF || `REC ${index + 1}`);
              }
            });
            
            if (responsaveisSemArquivo.length > 0) {
              setErrorOnForm(true);
              setErrorMessage(`É obrigatório preencher o campo 'Dados de Extensão de Uso' para: ${responsaveisSemArquivo.join(", ")}. Faça upload do arquivo para cada REC.`);
              window.scrollTo({ top: 0, behavior: 'smooth' });
              return;
            }
            
            setErrorOnForm(false);
            setErrorMessage("");

            // Ao re-enviar para regulatório, limpa decisões anteriores por REC
            // para que o backend receba statusRec=null em todas as RECs
            if (statusExtensao === STATUS_EXTENSAO_USO.ReprovadoPeloRegulatorio) {
              setRecAprovacoes({});
            }

            setRegulatoryModal(true);
          }}
          isLoading={loadingSubmit}
        />
      );
    }

    // Botão Finalizar Análise (Regulatório) - valida per-REC e submete; backend deriva status final
    let buttonAprovarRegulatorio = <Fragment />;
    if (
      (userIsAdmin || userIsRegulatorio) && 
      statusExtensao === STATUS_EXTENSAO_USO.AguardandoRegulatorio
    ) {
      buttonAprovarRegulatorio = (
        <Button
          title="Finalizar Análise"
          type="button"
          contained={true}
          onClick={() => {
            const currentFields = defaultForm.getValues("responsavelRecExtUso") as any[];
            // Verifica se RECs marcadas como "Não" têm motivo preenchido (via recAprovacoes)
            const reprovadaSemMotivo = (currentFields || []).filter((_: any, idx: number) => {
              const ap = recAprovacoes[idx];
              return ap?.statusRec === "Reprovado" && !ap?.motivoReprovacaoRec?.trim();
            });
            if (reprovadaSemMotivo.length > 0) {
              const nomes = reprovadaSemMotivo.map((_: any, i: number) => {
                const f = currentFields[i];
                return f?.descricaoUF || `REC ${f?.idRec || i + 1}`;
              });
              setErrorOnForm(true);
              setErrorMessage(`Informe o motivo da reprovação para: ${nomes.join(", ")}.`);
              window.scrollTo({ top: 0, behavior: 'smooth' });
              return;
            }
            setErrorOnForm(false);
            setErrorMessage("");
            setAproveModal(true);
          }}
          isLoading={loadingSubmit}
        />
      );
    }

    // Botão Reprovar global (Regulatório) removido: a reprovação agora é individual por REC via ParseGroup04
    let buttonReprovarRegulatorio = <Fragment />;

    // Botão Reprovar MAPA - visível quando status é "Aguardando MAPA"
    let buttonReprovarMAPA = <Fragment />;
    if (
      (userIsAdmin || userIsRegulatorio) && 
      statusExtensao === STATUS_EXTENSAO_USO.AguardandoMAPA
    ) {
      buttonReprovarMAPA = (
        <GenericButton
          title="Reprovado pelo MAPA"
          variant="btn-danger"
          onClick={() => {
            setReproveType("mapa");
            setReproveTxt("");
            setSuccessReprove(false);
            setReproveModal(true);
          }}
        />
      );
    }

    // Salvar edições (para Regulatório quando está em análise pelo regulatório)
    let buttonSalvarEdicoes = <Fragment />;
    if (
      (userIsAdmin || userIsRegulatorio) && 
      statusExtensao === STATUS_EXTENSAO_USO.AguardandoRegulatorio
    ) {
      buttonSalvarEdicoes = (
        <Button
          title="Salvar"
          type="submit"
          contained={true}
          onClick={() => {
            setSubmited(true);
          }}
          isLoading={loadingSubmit}
        />
      );
    }

    // Botão Aprovado pelo MAPA - visível quando status é "Aguardando MAPA"
    let buttonAprovarMAPA = <Fragment />;
    if (
      (userIsAdmin || userIsRegulatorio) && 
      statusExtensao === STATUS_EXTENSAO_USO.AguardandoMAPA
    ) {
      buttonAprovarMAPA = (
        <Button
          title="Aprovado pelo MAPA"
          type="button"
          contained={true}
          onClick={() => {
            setProtocolNumberTemp("");
            setProtocolDateTemp(null);
            setAproveMapaModal(true);
          }}
          isLoading={loadingSubmit}
        />
      );
    }

    return (
      <div className="button-position">
        {buttonVoltar}
        {buttonReprovarRegulatorio}
        {buttonReprovarMAPA}
        {buttonSalvarRascunho}
        {buttonSalvarEdicoes}
        {buttonEnviarResponsavel}
        {buttonEnviarRegulatorio}
        {buttonAprovarRegulatorio}
        {buttonAprovarMAPA}
      </div>
    );
  };

  const can_edit = (group: string) => {
    const userIsAdmin = isAdmin(roles);
    const userIsRegulatorio = isReguladorRegister(roles);
    const userIsRequester = isRequesterWriteRegister(roles);
    const userIsTecnico = isTecnicoExtensaoUso(roles);
    const userIsOnlyTecnico = isOnlyTecnicoExtensaoUso(roles);
    const statusExtensao = getStatusNormalizado();

    switch (group) {
      case "default":
        // Campos editáveis baseado no status da ExtensaoUso
        if (userIsAdmin) return true;

        // Requisitante pode editar em "Não Iniciado", "Aguardando Responsável" e "Reprovado pelo Regulatório"
        if (
          userIsRequester &&
          (statusExtensao === STATUS_EXTENSAO_USO.NaoIniciado ||
           statusExtensao === STATUS_EXTENSAO_USO.AguardandoResponsavel ||
           statusExtensao === STATUS_EXTENSAO_USO.ReprovadoPeloRegulatorio)
        )
          return true;

        // Regulatório pode editar em "Aguardando Regulatório" e "Aguardando MAPA"
        if (
          userIsRegulatorio &&
          (statusExtensao === STATUS_EXTENSAO_USO.AguardandoRegulatorio ||
           statusExtensao === STATUS_EXTENSAO_USO.AguardandoMAPA)
        )
          return true;

        return false;

      case "regioes":
        // Requisitante pode incluir/remover RECs enquanto ainda pode editar a extensão (antes do regulatório)
        if (userIsAdmin) return true;
        if (
          userIsTecnico &&
          statusExtensao === STATUS_EXTENSAO_USO.AguardandoResponsavel
        ) {
          return false;
        }
        if (userIsOnlyTecnico) return false;
        return (
          userIsRequester &&
          (statusExtensao === STATUS_EXTENSAO_USO.NaoIniciado ||
            isNovaExtensao ||
            statusExtensao === STATUS_EXTENSAO_USO.AguardandoResponsavel ||
            statusExtensao === STATUS_EXTENSAO_USO.ReprovadoPeloRegulatorio)
        );

      case "dados_rec":
        // Dados REC podem ser editados em "Aguardando Responsável" ou "Reprovado Pelo Regulatório"
        // Técnico PODE editar dados REC para fazer correções e reenviar
        if (userIsAdmin) return true;
        if (
          userIsTecnico &&
          (statusExtensao === STATUS_EXTENSAO_USO.AguardandoResponsavel ||
           statusExtensao === STATUS_EXTENSAO_USO.ReprovadoPeloRegulatorio)
        )
          return true;
        return (
          userIsRequester &&
          (statusExtensao === STATUS_EXTENSAO_USO.AguardandoResponsavel ||
           statusExtensao === STATUS_EXTENSAO_USO.ReprovadoPeloRegulatorio)
        );

      case "protocolo":
        // Protocolo só pode ser editado pelo regulatório em "Aguardando MAPA"
        if (userIsAdmin) return true;
        return (
          userIsRegulatorio &&
          statusExtensao === STATUS_EXTENSAO_USO.AguardandoMAPA
        );

      case "preenchimento_observacoes":
        return true;

      case "save":
        return true; // Lógica de botões já controla quem pode salvar

      case "draft":
        return statusExtensao === STATUS_EXTENSAO_USO.NaoIniciado;

      case "protocol":
        if (!userIsRegulatorio && !userIsAdmin) return false;

        if (
          dataCultivaresRegistro?.status !==
            STATUS_REGISTER.ValidadoPeloRegulatorio &&
          dataCultivaresRegistro?.status !==
            STATUS_REGISTER.ValidadoPelaSGFITO &&
          dataCultivaresRegistro?.status !==
            STATUS_REGISTER.ValidadoPelaSGFITOComAlteracoes
        )
          return false;

        return true;

      case "adjustement":
        if (
          dataCultivaresRegistro?.status ===
          STATUS_REGISTER.ReprovadoPeloRegulatorio
        )
          return true;

        return false;

      case "secao_apos_dados_rec":
        // Seção de Protocolo só aparece quando já está "Aprovado pelo MAPA"
        // Quando está "Aguardando Aprovação MAPA", o modal de aprovação cuida do preenchimento
        if (statusExtensao === STATUS_EXTENSAO_USO.AprovadoPeloMAPA) {
          return true; // Mostra para todos visualizarem os dados do protocolo
        }
        return false;
    }

    return true;
  };

  const actionAdaptationRegions = (props: any) => {
    if (!can_edit("regioes")) {
      return <span className="text-muted">—</span>;
    }
    return (
      <DeleteButton
        onClick={() => {
          setSelectedAdaptationRegions([]);
          let arr: any = [];
          const { node } = props;

          node.gridApi.updateRowData({ remove: [node.data] });

          node.gridApi.forEachNode(({ data }: any) => arr.push(data));
          setSelectedAdaptationRegions([...arr]);
        }}
      />
    );
  };

  const ParseMessages = () => {
    return (
      <Fragment>
        <If condition={successfulResponse && !errorOnSubmit}>
          <AlertSuccess message={STRINGS.ALERT_MESSAGE_SUCCESS} />
        </If>

        {/*<If condition={!successfulResponse && errorOnSubmit}>*/}
        {/*  {errorOnSubmit !== null && errorOnSubmit?.message?.length > 0 ? (*/}
        {/*    errorOnSubmit.message.map((error: string) => (*/}
        {/*      <AlertError message={error} />*/}
        {/*    ))*/}
        {/*  ) : (*/}
        {/*    <AlertError message={STRINGS.ALERT_GENERIC_ERROR} />*/}
        {/*  )}*/}
        {/*</If>*/}

        <If condition={loadingRequestData}>
          <span className="loading-page">
            <Loading />
          </span>
        </If>

        <If condition={successDraft && !errorDraft}>
          <AlertSuccess message={STRINGS.SUCCESS_DRAFT} />
        </If>
        <If condition={!successDraft && errorDraft}>
          {errorDraft !== null && errorDraft?.message?.length > 0 ? (
            errorDraft.message.map((error: string, idx: number) => (
              <AlertError key={idx} message={error} />
            ))
          ) : (
            <AlertError message={typeof errorDraft === 'string' ? errorDraft : STRINGS.ALERT_GENERIC_ERROR} />
          )}
        </If>
        <If condition={errorOnForm}>
          <AlertError message={errorMessage || STRINGS.ALERT_WARNING_FIELDS} />
        </If>
      </Fragment>
    );
  };

  const ParseGroup00 = () => {
    return (
      <Fieldset title="Registro">
        <Form.Row className="row-custom">
          <Form.Group as={Col} md={4}>
            <Form.Label>Código</Form.Label>
			<Form.Control
              type="hidden"
              name="codigo"
              ref={defaultForm.register}
              value={dataCultivaresRegistro?.codigo}
            />
            <Form.Control
              value={dataCultivaresRegistro?.codigo}
              name="codigoLabel"
              readOnly
            />
          </Form.Group>
          <Form.Group as={Col} md={4}>
            <Form.Label>Status Registro RNC</Form.Label>
            <Form.Control
              value={dataCultivaresRegistro?.statusDescricao}
              readOnly
            />
            {/* Campo hidden para o status da ExtensaoUso - controlado pelo react-hook-form */}
            <Form.Control
              type="hidden"
              name="status"
              ref={defaultForm.register}
            />
          </Form.Group>
        </Form.Row>

        <If
          condition={
            dataCultivaresRegistro?.status === STATUS_REGISTER.Cancelado
          }
        >
          <Form.Row className="row-custom">
            <Form.Group as={Col} md={4}>
              <Form.Label>Motivo Cancelamento</Form.Label>
              <Form.Control
                ref={defaultForm.register}
                name="cancelReason"
                readOnly
              />
            </Form.Group>
          </Form.Row>
        </If>
      </Fieldset>
    );
  };

  const ParseGroup01 = () => {
    return (
      <Fieldset
        title="1. Identificação"
        required
        disabled={!can_edit("default")}
      >
        <Form.Row className="row-custom">
          <Form.Group as={Col}>
            <Form.Label>Denominação da Cultivar/Linhagem</Form.Label>
            <Form.Control
              type="text"
              placeholder="Denominação da Cultivar/Linhagem"
              readOnly
              value={dataCultivaresRegistro?.denominacaoDaCultivarLinhagem || ""}
            />
          </Form.Group>
        </Form.Row>

        <Form.Row className="row-custom">
          <Form.Group as={Col}>
            <Form.Label>Nome comum da espécie</Form.Label>
            <Form.Control
              type="hidden"
              name="speciesId"
              ref={defaultForm.register}
            />
            <Form.Control
              type="hidden"
              name="speciesName"
              ref={defaultForm.register}
            />
            <Form.Control
              type="text"
              placeholder="Nome comum da espécie"
              readOnly
              name="speciesNameLabel"
              value={cultureDetails?.nome}
            />
          </Form.Group>

          <Form.Group as={Col}>
            <Form.Label>Nome científico da espécie</Form.Label>
			      <Form.Control
              type="hidden"
              name="scientificName"
              ref={defaultForm.register}
            />
            <Form.Control
              type="text"
              placeholder="Nome científico da espécie"
              readOnly
              name="scientificNameLabel"
              value={cultureDetails?.descricao}
            />
          </Form.Group>
        </Form.Row>
      </Fieldset>
    );
  };

  const Responsaveis = () => {
    return (
      <Fieldset title="Responsáveis">
        {recsSemVinculo.length > 0 && (
          <Alert variant="warning" className="mb-3">
            As seguintes RECs não possuem responsável técnico cadastrado:{" "}
            {recsSemVinculo.join(", ")}. Cadastre em Cultivares &gt; Cadastros &gt;
            Responsáveis REC (Extensão de Uso).
          </Alert>
        )}
        {fields.map((field, index) => {
          const en =
            defaultForm.formState.errors?.responsavelRecExtUso?.[index]
              ?.nomeResponsavel;
          const ee =
            defaultForm.formState.errors?.responsavelRecExtUso?.[index]
              ?.emailResponsavel;

          return (
            <Form.Row className="row-custom" key={field.id}>
              <Controller
                key={`${field.id}-idRec`}
                control={defaultForm.control}
                name={`responsavelRecExtUso[${index}].idRec` as any}
                defaultValue={field.idRec ?? ""}
                render={() => <></>}
              />
              <Controller
                key={`${field.id}-descricaoUF`}
                control={defaultForm.control}
                name={`responsavelRecExtUso[${index}].descricaoUF` as any}
                defaultValue={field.descricaoUF ?? ""}
                render={() => <></>}
              />
              <Form.Group as={Col} md={4}>
                <Form.Label>Nome do técnico</Form.Label>
                <Controller
                  control={defaultForm.control}
                  name={`responsavelRecExtUso[${index}].nomeResponsavel` as any}
                  defaultValue={field.nomeResponsavel ?? ""}
                  render={({ value }) => (
                    <Form.Control
                      readOnly
                      disabled
                      value={String(value ?? "")}
                      className="bg-light"
                    />
                  )}
                />
                {en && (
                  <small className="text-danger d-block mt-1">
                    {(en as any)?.message}
                  </small>
                )}
              </Form.Group>
              <Form.Group as={Col} md={4}>
                <Form.Label>E-mail do técnico</Form.Label>
                <Controller
                  control={defaultForm.control}
                  name={`responsavelRecExtUso[${index}].emailResponsavel` as any}
                  defaultValue={field.emailResponsavel ?? ""}
                  render={({ value }) => (
                    <Form.Control
                      readOnly
                      disabled
                      value={String(value ?? "")}
                      className="bg-light"
                    />
                  )}
                />
                {ee && (
                  <small className="text-danger d-block mt-1">
                    {(ee as any)?.message}
                  </small>
                )}
              </Form.Group>
              <Form.Group as={Col} md={4}>
                <Form.Label>REC</Form.Label>
                <Form.Control
                  value={field.descricaoUF ?? ""}
                  disabled
                  readOnly
                />
              </Form.Group>
              <Form.Group as={Col} md={12}>
                <Form.Label>Observação</Form.Label>
                <Controller
                  key={`${field.id}-observacao`}
                  control={defaultForm.control}
                  name={`responsavelRecExtUso[${index}].observacao` as any}
                  defaultValue={(field as any).observacao ?? ""}
                  render={({ onChange, value }) => (
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Insira as observações necessárias"
                      disabled={!(can_edit("dados_rec") || can_edit("default"))}
                      value={value ?? ""}
                      onChange={(e) => onChange(e.target.value)}
                    />
                  )}
                />
              </Form.Group>
            </Form.Row>
          );
        })}
      </Fieldset>
    );
  };

  // Monta lista combinada de RECs do registro (originais + extensões anteriores)
  const getRecsZoneamentoAtual = () => {
    const recsOriginais = (recsInfo?.recsRegistroOriginal || registroAdaptationRegions).map((rec: any) => ({
      id: rec.id,
      descricao: rec.descricao,
      origem: "Registro Original"
    }));
    
    const recsExtensoes = (recsInfo?.recsExtensoesAnteriores || []).map((rec: any) => ({
      id: rec.id,
      descricao: rec.descricao,
      origem: rec.anoAprovacao ? `Extensão de Uso (${rec.anoAprovacao})` : "Extensão de Uso"
    }));
    
    return [...recsOriginais, ...recsExtensoes];
  };

  const ParseGroup02 = () => {
    const recsZoneamento = getRecsZoneamentoAtual();
    
    return (
      <>
        {/* Seção 2: Zoneamento Atual do Registro */}
        <Fieldset title={"2. Zoneamento Atual"} disabled={true}>
          <Form.Row className="row-custom">
            <Form.Group as={Col} md={12}>
              <Form.Label>
                Região de adaptação do registro<em>*</em>
              </Form.Label>
            </Form.Group>
          </Form.Row>
          <If condition={recsZoneamento.length > 0}>
            <div
              className="ag-theme-alpine"
              style={{ height: "250px", width: "100%" }}
            >
              <AgGridReact
                rowData={recsZoneamento}
                defaultColDef={{ flex: 1, sortable: true }}
              >
                <AgGridColumn headerName="Região" field="descricao" flex={2} />
                <AgGridColumn 
                  headerName="Origem" 
                  field="origem" 
                  flex={1}
                  cellStyle={(params: any) => ({
                    color: params.value?.includes("Extensão") ? "#28a745" : "#495057",
                    fontStyle: params.value?.includes("Extensão") ? "italic" : "normal"
                  })}
                />
              </AgGridReact>
            </div>
          </If>
          <If condition={recsZoneamento.length === 0}>
            <Form.Row className="row-custom">
              <Form.Group as={Col} md={12}>
                <Form.Label style={{ color: "#999", fontStyle: "italic" }}>
                  Nenhuma região de adaptação cadastrada no registro
                </Form.Label>
              </Form.Group>
            </Form.Row>
          </If>
          
          {/* Info sobre RECs disponíveis para nova extensão */}
          <If condition={recsInfo?.recsDisponiveis && recsInfo.recsDisponiveis.length > 0}>
            <Form.Row className="row-custom mt-2">
              <Form.Group as={Col} md={12}>
                <small className="text-muted">
                  <i className="fa fa-info-circle"></i> {recsInfo?.recsDisponiveis?.length || 0} REC(s) ainda disponíveis para extensão de uso.
                </small>
              </Form.Group>
            </Form.Row>
          </If>
        </Fieldset>

        {/* Seção 3: sem disabled no fieldset — disabled em <fieldset> bloqueia envio/validação RHF dos filhos */}
        <Fieldset title={"3. Extensão de Uso"}>
          <Form.Row className="row-custom">
            <Form.Group className="label-btn">
              <Form.Label>
                Novas Localidades<em>*</em>
              </Form.Label>
              <AddButton
                type="button"
                onClick={() => {
                  setChooseAdaptationRegions(true);
                  setUnselectedAdaptationRegion(selectedAdaptationRegions);
                  setChoosenAdaptationRegions([]);
                }}
                disabled={
                  !can_edit("regioes") ||
                  !recsInfo?.recsDisponiveis?.length
                }
              />
            </Form.Group>
          </Form.Row>
          <If condition={selectedAdaptationRegions.length}>
            <div
              className="ag-theme-alpine"
              style={{ height: "200px", width: "100%" }}
            >
              <AgGridReact
                rowData={selectedAdaptationRegions}
                getRowNodeId={(data: Regions) =>
                  `${data?.id ?? ""}_${(data?.descricao ?? "")
                    .replace(/\s+/g, " ")
                    .trim()}`
                }
                defaultColDef={{ flex: 1, sortable: true }}
                frameworkComponents={{
                  action: actionAdaptationRegions,
                }}
              >
                <AgGridColumn headerName="Região" field="descricao" />
                <AgGridColumn headerName="Ação" cellRenderer="action" />
              </AgGridReact>
            </div>
          </If>
          <If condition={chooseAdaptationRegions}>
          <Modal
            title="Lista de regiões de adaptação"
            onHide={() => setChooseAdaptationRegions(false)}
            show={true}
          >
            <div
              className="ag-theme-alpine ag-cell-custom"
              style={{ height: "300px", width: "100%" }}
            >
              <AgGridReact
                rowData={unselectedAdaptationRegions}
                defaultColDef={{ flex: 1, sortable: true }}
                suppressRowClickSelection={true}
                onRowSelected={onRowSelectedAdaptationRegions}
                rowSelection={"multiple"}
              >
                <AgGridColumn
                  headerName="Selecionar"
                  headerCheckboxSelection
                  checkboxSelection={true}
                  pinned="left"
                />
                <AgGridColumn headerName="Região" field="descricao" />
              </AgGridReact>
            </div>
            <div className="button-position">
              <Button
                title="Cancelar"
                contained={false}
                type="button"
                onClick={() => setChooseAdaptationRegions(false)}
              />
              <Button
                title="Inserir"
                contained={true}
                type="button"
                onClick={() => {
                  setChooseAdaptationRegions(false);
                  setInsertAdaptationRegions(true);
                  setSelectedAdaptationRegions((prev) => {
                    const chave = (r: { id?: number; descricao?: string }) =>
                      `${r.id ?? ""}::${(r.descricao ?? "").trim()}`;
                    const visto = new Set(prev.map(chave));
                    const proximo = [...prev];
                    for (const r of choosenAdaptationRegions) {
                      const k = chave(r);
                      if (!visto.has(k)) {
                        visto.add(k);
                        proximo.push(r);
                      }
                    }
                    return proximo;
                  });
                }}
                disabled={!choosenAdaptationRegions.length}
              />
            </div>
          </Modal>
        </If>

        {Responsaveis()}
      </Fieldset>
      </>
    );
  };

  const ParseGroup03 = () => {
  return (
    <Fieldset title="3. Mapa - Regiões Edafoclimáticas">
      <Form.Row className="row-custom">
          <Form.Group as={Col} md={12}>
            <img
              src={mapaImg}
              alt="Mapa das Regiões Edafoclimáticas"
              style={{
                width: "100%",
                maxWidth: "800px",
                borderRadius: "8px",
                boxShadow: "0 0 8px rgba(0,0,0,0.15)",
              }}
            />
          </Form.Group>
        </Form.Row>
        <Form.Row className="row-custom">
          <Form.Group as={Col} md={12}>
            <AddButton
              type="button"
              onClick={() => handleDownload(mapaImg, 'recs-mapa.png')}
              otherTitle="Baixar"
              isDownload={true}
            />
          </Form.Group>
        </Form.Row >
    </Fieldset>
  );
};

  const ParseGroup04 = (field: any, index: number) => {
    const { idRec, arquivoRec } = field;
    const responsavelByRequest = extensaoUso?.responsavelRecExtUso?.find((r) => +r.idRec === +idRec);

    const nomeArquivo =
        typeof arquivoRec === "string"
          ? arquivoRec
          : Array.isArray(arquivoRec)
          ? arquivoRec[0]?.name
          : arquivoRec?.name;

    const nomeArquivoDownload = nomeArquivo || responsavelByRequest?.arquivoRec?.name || "";

    const pathArquivo =
        typeof arquivoRec === "string"
          ? arquivoRec
          : Array.isArray(arquivoRec)
          ? arquivoRec[0]?.caminhoArqRec
          : arquivoRec?.caminhoArqRec;

    const pathArquivoDownload = pathArquivo || responsavelByRequest?.arquivoRec?.caminhoArqRec || "";

    const userIsRegulatorioLocal = isReguladorRegister(roles);
    const userIsAdminLocal = isAdmin(roles);
    const podeAnalisarRec = (userIsAdminLocal || userIsRegulatorioLocal) &&
      getStatusNormalizado() === STATUS_EXTENSAO_USO.AguardandoRegulatorio;

    // Usa o estado local recAprovacoes para reatividade garantida (watch não funciona bem
    // em campos não registrados de useFieldArray sem hidden inputs)
    const aprovacaoAtual = recAprovacoes[index] ?? {
      statusRec: (field as any).statusRec ?? "",
      motivoReprovacaoRec: (field as any).motivoReprovacaoRec ?? "",
    };
    const recAprovada = aprovacaoAtual.statusRec !== "Reprovado";
    const motivoRecAtual = aprovacaoAtual.motivoReprovacaoRec;

    const atualizarAprovacao = (statusRec: string, motivoReprovacaoRec?: string) => {
      setRecAprovacoes(prev => ({
        ...prev,
        [index]: {
          statusRec,
          motivoReprovacaoRec: motivoReprovacaoRec !== undefined ? motivoReprovacaoRec : (prev[index]?.motivoReprovacaoRec ?? ""),
        },
      }));
    };

    return (
      <Fieldset
        title={`Dados para a Rec ${field.descricaoUF || field.descricao || ""}`}
        required
        disabled={!can_edit("dados_rec")}
      >
        <Form.Row className="row-custom">
          <Form.Group as={Col}>
            <Form.Label>
              Dados de Extensão de Uso<em>*</em>
            </Form.Label>
          </Form.Group> 
        </Form.Row>

        <Form.Row className="row-custom">
          <Form.Group as={Col} md={6}>
            <AddButton
			type="button"
            onClick={handleDownloadModeloRec}
			otherTitle="Baixar modelo"
			isDownload={true}
			/>

            <Form.File
              name={extensaoUsoArquivoRecInputName(index)}
              label={
                arquivosRemovidos.has(index)
                  ? "Selecionar arquivo (.doc, .docx)"
                  : arquivoRecPendenteNome[index] ||
                    "Selecionar arquivo (.doc, .docx)"
              }
              data-browse="upload"
              accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              custom
              onChange={(e: any) => {
                const f = e.target.files?.[0];
                setArquivoRecPendenteNome((prev) => ({
                  ...prev,
                  [index]: f?.name || "",
                }));
                if (f) {
                  setArquivosRemovidos((prev) => {
                    const next = new Set(prev);
                    next.delete(index);
                    return next;
                  });
                }
              }}
            />
          </Form.Group>
          
          <If condition={nomeArquivoDownload && !arquivosRemovidos.has(index)}>
            <FileExistent text={nomeArquivoDownload} 
              onDownload={(e: any) => {
                e.preventDefault();
                downloadFile(nomeArquivoDownload, pathArquivoDownload);
              }}
              onClick={can_edit("dados_rec") ? () => {
                setArquivosRemovidos((prev) => new Set(prev).add(index));
                setArquivoRecPendenteNome((prev) => {
                  const next = { ...prev };
                  delete next[index];
                  return next;
                });
                const inputEl = document.querySelector(
                  `input[name="${extensaoUsoArquivoRecInputName(index)}"]`
                ) as HTMLInputElement | null;
                if (inputEl) inputEl.value = "";
                defaultForm.setValue(`responsavelRecExtUso[${index}].caminhoArqRec`, null);
                defaultForm.setValue(`responsavelRecExtUso[${index}].idArqRec`, null);
              } : undefined}
            />
          </If>

          
        </Form.Row>

        {/* Seção de aprovação individual por REC — padrão radio Sim/Não (mesmo padrão do RegistroRNC) */}
        <If condition={podeAnalisarRec}>
          <Fieldset title="Aprovação" required disabled={false}>
            <div className="option-cultivar">
              <span>Aprovado?<em>* </em></span>
              <Form.Check
                value="sim"
                type="radio"
                aria-label="radio aprovado"
                label="Sim"
                onChange={() => atualizarAprovacao("Aprovado", "")}
                checked={recAprovada}
              />
              <Form.Check
                value="não"
                type="radio"
                aria-label="radio reprovado"
                label="Não"
                onChange={() => atualizarAprovacao("Reprovado")}
                checked={!recAprovada}
              />
            </div>

            <If condition={!recAprovada}>
              <div className="option-cultivar">
                <Form.Group as={Col}>
                  <Form.Label>Motivo da Reprovação<em>*</em></Form.Label>
                  <div style={motivoRecAtual ? { border: '2px solid #dc3545', borderRadius: '0.25rem', padding: '0' } : {}}>
                    <Form.Control
                      as="textarea"
                      value={motivoRecAtual}
                      onChange={({ target }: any) => atualizarAprovacao("Reprovado", target.value)}
                      placeholder="Insira o motivo da reprovação"
                      maxLength={500}
                      rows={3}
                      isInvalid={!recAprovada && !motivoRecAtual}
                      style={motivoRecAtual ? { border: 'none', boxShadow: 'none', outline: 'none' } : {}}
                    />
                  </div>
                  <If condition={!recAprovada && !motivoRecAtual}>
                    <Form.Control.Feedback type="invalid" style={{ display: 'block', color: '#dc3545' }}>
                      Informe o motivo da reprovação.
                    </Form.Control.Feedback>
                  </If>
                </Form.Group>
              </div>
            </If>
          </Fieldset>
        </If>

        {/* Exibe status e motivo de reprovação por REC após análise (modo leitura) */}
        <If condition={
          !podeAnalisarRec &&
          (getStatusNormalizado() === STATUS_EXTENSAO_USO.ReprovadoPeloRegulatorio ||
           getStatusNormalizado() === STATUS_EXTENSAO_USO.AguardandoMAPA ||
           getStatusNormalizado() === STATUS_EXTENSAO_USO.AprovadoPeloMAPA) &&
          !!(field as any).statusRec
        }>
          <Form.Row className="row-custom">
            <Form.Group as={Col} md={12}>
              <Form.Label>
                Resultado da Análise Regulatória
              </Form.Label>
              <Form.Control
                as="textarea"
                value={
                  (field as any).statusRec === "Reprovado"
                    ? ((field as any).motivoReprovacaoRec || "Sem motivo informado")
                    : "Aprovada"
                }
                disabled
                readOnly
                rows={2}
                style={{
                  borderColor: (field as any).statusRec === "Reprovado" ? '#dc3545' : '#198754',
                  borderWidth: '2px',
                  color: (field as any).statusRec === "Reprovado" ? '#dc3545' : '#198754',
                  fontWeight: 'bold'
                }}
              />
            </Form.Group>
          </Form.Row>
        </If>

        {/* Motivo global de reprovação (retrocompatibilidade com registros anteriores à análise per-REC) */}
        <If condition={
          (getStatusNormalizado() === STATUS_EXTENSAO_USO.ReprovadoPeloRegulatorio ||
           getStatusNormalizado() === STATUS_EXTENSAO_USO.ReprovadoPeloMAPA) &&
          !!extensaoUso?.motivoReprovacao &&
          !(field as any).statusRec
        }>
          <Form.Row className="row-custom">
            <Form.Group as={Col} md={12}>
              <Form.Label>
                {getStatusNormalizado() === STATUS_EXTENSAO_USO.ReprovadoPeloMAPA
                  ? "Motivo da Reprovação pelo MAPA"
                  : "Motivo da Reprovação"}
              </Form.Label>
              <Form.Control
                as="textarea"
                value={extensaoUso?.motivoReprovacao || ""}
                disabled
                readOnly
                rows={3}
                style={{ borderColor: '#dc3545', borderWidth: '2px' }}
              />
            </Form.Group>
          </Form.Row>
        </If>
      </Fieldset>
    );
  }; 

  const ParseGroup05 = () => {
    return (
      <Fieldset title="4. Protocolo" disabled={true}>
          <Form.Row className="row-custom">
            <Form.Group as={Col} md={4}>
				<Form.Label>Número Protocolo</Form.Label>
				<Form.Control
					ref={defaultForm.register}
					name="protocolNumber"
					type="text"
					disabled
					readOnly
				/>
            </Form.Group>
              <Form.Group as={Col}>
		  		<Form.Label>Data Protocolo</Form.Label>
                <br />
                <Controller
                  control={defaultForm.control}
                  name="protocolDate"
                  render={({ value }) => (
                    <DatePicker
                      selected={value}
                      onChange={() => {}}
                      className="input-custom-to-date"
                      locale="pt-BR"
                      dateFormat="dd/MM/yyyy"
                      disabled
                      readOnly
                    />
                  )}
                />
            </Form.Group>
          </Form.Row>
      </Fieldset>
    );
  };

  return (
    <div className="container container-fluid">
      {ParseMessages()}

      <If condition={!loadingRequestData}>
        <h3>{isNovaExtensao ? "Nova Extensão de Uso" : "Edição de Extensão de Uso"}</h3>
        
        {/* Mensagem de nova extensão */}
        <If condition={isNovaExtensao}>
          <div className="alert alert-success mb-3">
            <strong><i className="fa fa-plus-circle"></i> Criando nova extensão de uso</strong>
            <p className="mb-0">A extensão de uso anterior foi aprovada pelo MAPA. Agora você pode criar uma nova extensão de uso para este registro, selecionando as RECs que ainda não foram utilizadas.</p>
          </div>
        </If>

        {/* Mensagem quando extensão está aprovada e há RECs disponíveis */}
        <If condition={
          !isNovaExtensao && 
          getStatusNormalizado() === STATUS_EXTENSAO_USO.AprovadoPeloMAPA && 
          recsInfo?.recsDisponiveis && 
          recsInfo.recsDisponiveis.length > 0
        }>
          <div className="alert alert-info mb-3">
            <strong><i className="fa fa-info-circle"></i> Extensão de Uso Aprovada</strong>
            <p className="mb-2">Esta extensão de uso já foi aprovada pelo MAPA e não pode ser editada.</p>
            <p className="mb-2">
              Existem ainda <strong>{recsInfo?.recsDisponiveis?.length || 0} REC(s) disponíveis</strong> para novas extensões de uso.
            </p>
            <button 
              type="button"
              className="btn btn-success"
              onClick={() => {
                // Navega com query param para forçar re-render sem reload
                history.replace({
                  pathname: '/cultivares/extensao-uso/editar',
                  search: `?id=${registroId}&novaExtensao=true`,
                  state: {
                    id: registroId,
                    novaExtensao: true
                  }
                });
              }}
            >
              <i className="fa fa-plus"></i> Criar Nova Extensão de Uso
            </button>
          </div>
        </If>

        {/* Mensagem quando extensão está aprovada e NÃO há RECs disponíveis */}
        <If condition={
          !isNovaExtensao && 
          getStatusNormalizado() === STATUS_EXTENSAO_USO.AprovadoPeloMAPA && 
          (!recsInfo?.recsDisponiveis || recsInfo.recsDisponiveis.length === 0)
        }>
          <div className="alert alert-secondary mb-3">
            <strong><i className="fa fa-check-circle"></i> Extensão de Uso Completa</strong>
            <p className="mb-0">Esta extensão de uso foi aprovada pelo MAPA. Todas as RECs da cultura já foram utilizadas no registro ou em extensões de uso.</p>
          </div>
        </If>

        <Form
          onSubmit={(e) => {
            syncResponsavelRecIdsComRegioes();
            defaultForm.handleSubmit(onSubmit, onError)(e);
          }}
        >
          {ParseGroup00()}
          {ParseGroup01()}
          {ParseGroup02()}
          {ParseGroup03()}

          <If condition={
            isAdmin(roles) || isTecnicoExtensaoUso(roles) || isReguladorRegister(roles)
            || isRequesterWriteRegister(roles) || isRequesterReadRegister(roles)
          }>
          {fields.map((field, index) => {
            if (
              isOnlyTecnicoExtensaoUso(roles) &&
              (field as any).emailResponsavel?.toLowerCase() !== loggedUserEmail
            ) {
              return null;
            }
            return ParseGroup04(field, index);
          })}
          </If>

          <If condition={can_edit("secao_apos_dados_rec")}>
            {ParseGroup05()}
          </If>
          {parseButton()}
        </Form>
      </If>

      {/* Modal de Reprovação Global (MAPA) */}
      <If condition={reproveModal}>
        <ReasonModal
          show={reproveModal}
          onHide={() => setReproveModal(false)}
          title={reproveType === "regulatorio" ? "Reprovar Extensão de Uso" : "Reprovar pelo MAPA"}
          actionMsg="Confirmar Reprovação"
          disabled={reproveTxt.length === 0}
          loading={loadingReprove}
          onConfirm={handleReprove}
        >
          <If condition={successReprove}>
            <AlertSuccess message="Extensão de uso reprovada com sucesso!" />
          </If>
          <Form.Label>
            Informe o motivo da reprovação:
          </Form.Label>
          <Form.Control
            value={reproveTxt}
            onChange={({ target }: any) => setReproveTxt(target.value)}
            as="textarea"
            maxLength={500}
            rows={4}
            placeholder="Digite o motivo da reprovação..."
          />
        </ReasonModal>
      </If>


      {/* Modal de Confirmação para Envio ao Técnico */}
      <If condition={enviarTecnicoModal}>
        <ConfirmationModal
          show={enviarTecnicoModal}
          onHide={() => setEnviarTecnicoModal(false)}
          title="Deseja enviar para o Técnico?"
          confirmationMsg="Enviar"
          onConfirm={() => {
            setEnviarTecnicoModal(false);
            const tr = defaultForm.getValues("tipoRegistro");
            if (tr == null) {
              (defaultForm as any).setValue(
                "tipoRegistro",
                TIPO_REGISTRO_VALUES[0],
                false
              );
            }
            syncResponsavelRecIdsComRegioes();
            defaultForm.setValue("status", STATUS_EXTENSAO_USO.AguardandoResponsavel);
            setSubmited(true);
            defaultForm.handleSubmit(onSubmit, onError)();
          }}
          loading={loadingSubmit}
          variant="success"
          color="white"
        >
          <p>Essa operação não poderá ser desfeita.</p>
        </ConfirmationModal>
      </If>

      {/* Modal de Confirmação para Envio ao Regulatório */}
      <If condition={regulatoryModal}>
        <ConfirmationModal
          show={regulatoryModal}
          onHide={() => setRegulatoryModal(false)}
          title={STRINGS.REGULATORY_TITLE}
          confirmationMsg={STRINGS.REGULATORY_ACTION}
          onConfirm={() => {
            setRegulatoryModal(false);
            syncResponsavelRecIdsComRegioes();
            defaultForm.setValue("status", STATUS_EXTENSAO_USO.AguardandoRegulatorio);
            setSubmited(true);
            defaultForm.handleSubmit(onSubmit, onError)();
          }}
          loading={loadingSubmit}
          variant="success"
          color="white"
        >
          <p>{STRINGS.REGULATORY_MESSAGE}</p>
        </ConfirmationModal>
      </If>

      {/* Modal de Confirmação para Aprovação / Finalizar Análise */}
      <If condition={aproveModal}>
        <ConfirmationModal
          show={aproveModal}
          onHide={() => setAproveModal(false)}
          title={
            getStatusNormalizado() === STATUS_EXTENSAO_USO.AguardandoRegulatorio
              ? "Finalizar Análise Regulatória"
              : STRINGS.APROVE_TITLE
          }
          confirmationMsg={STRINGS.APROVE_CONFIRMATION}
          onConfirm={() => {
            setAproveModal(false);
            if (getStatusNormalizado() === STATUS_EXTENSAO_USO.AguardandoRegulatorio) {
              // Backend deriva o status final (AguardandoMAPA ou ReprovadoPeloRegulatorio) com base nos RECs
              defaultForm.setValue("status", STATUS_EXTENSAO_USO.AguardandoRegulatorio);
            } else {
              defaultForm.setValue("status", STATUS_EXTENSAO_USO.AguardandoMAPA);
            }
            syncResponsavelRecIdsComRegioes();
            setSubmited(true);
            defaultForm.handleSubmit(onSubmit, onError)();
          }}
          loading={loadingSubmit}
          variant="success"
          color="white"
        >
          <p>
            {getStatusNormalizado() === STATUS_EXTENSAO_USO.AguardandoRegulatorio
              ? "Confirma a finalização da análise? O sistema irá aprovar ou reprovar a extensão de uso com base nos status individuais de cada REC."
              : STRINGS.APROVE_MSG}
          </p>
        </ConfirmationModal>
      </If>

      {/* Modal de Aprovação pelo MAPA com Protocolo */}
      <If condition={aproveMapaModal}>
        <ConfirmationModal
          show={aproveMapaModal}
          onHide={() => setAproveMapaModal(false)}
          title="Aprovar pelo MAPA"
          confirmationMsg="Aprovar"
          disabled={!protocolNumberTemp}
          onConfirm={async () => {
            setAproveMapaModal(false);
            setLoadingSubmit(true);
            
            const values = defaultForm.getValues();
            values.protocolNumber = protocolNumberTemp;
            values.protocolDate = protocolDateTemp;
            values.status = STATUS_EXTENSAO_USO.AprovadoPeloMAPA;
            
            // IMPORTANTE: Determina o código e id corretos da extensão
            let codigoParaEnviar = values.codigo;
            let idParaEnviar: number | null = null;
            
            if (extensaoUso?.codigo) {
              codigoParaEnviar = extensaoUso.codigo;
              idParaEnviar = extensaoUso.id || null;
            } else if (codigoExtensao && codigoExtensao !== dataCultivaresRegistro?.codigo) {
              codigoParaEnviar = codigoExtensao;
            }
            
            try {
              const valuesAjustados = { ...values };
              
              // Aplica o código e id corretos
              valuesAjustados.codigo = codigoParaEnviar;
              if (idParaEnviar) {
                (valuesAjustados as any).id = idParaEnviar;
              }
              
              if (valuesAjustados.responsavelRecExtUso) {
                valuesAjustados.responsavelRecExtUso = valuesAjustados.responsavelRecExtUso.map((resp: any, index: number) => {
                  const { arquivoRec, ...rest } = resp;
                  if (arquivosRemovidos.has(index)) {
                    return { ...rest, arquivoRec: null };
                  }
                  if (arquivoRec && (arquivoRec.caminhoArqRec || arquivoRec.id)) {
                    return {
                      ...rest,
                      arquivoRec: {
                        id: arquivoRec.id || null,
                        idRec: Number(resp.idRec) || arquivoRec.idRec,
                        caminhoArqRec: arquivoRec.caminhoArqRec,
                        name: arquivoRec.name
                      }
                    };
                  }
                  return rest;
                });
              }
              
              const request = await api.post(`api/v1/cultivares/ExtensaoUso`, valuesAjustados);
              
              if (request?.status === 200 || request?.status === 201) {
                setSuccessfulResponse(true);
                history.push({ pathname: '/cultivares/extensao-uso/consultar' });
              } else {
                setErrorOnSubmit(request.data);
              }
            } catch (error: any) {
              console.error(error);
              setErrorOnSubmit(error?.response?.data || "Erro ao aprovar");
            } finally {
              setLoadingSubmit(false);
            }
          }}
          loading={loadingSubmit}
          variant="success"
          color="white"
        >
          <p>Preencha os dados do protocolo para aprovar a Extensão de Uso pelo MAPA.</p>
          <Form.Row className="row-custom">
            <Form.Group as={Col} md={6}>
              <Form.Label>
                Número do Protocolo<em>*</em>
              </Form.Label>
              <Form.Control
                value={protocolNumberTemp}
                onChange={({ target }: any) => setProtocolNumberTemp(target.value)}
                maxLength={50}
                placeholder="Informe o número do protocolo"
              />
            </Form.Group>
            <Form.Group as={Col} md={6}>
              <Form.Label>
                Data do Protocolo
              </Form.Label>
              <br />
              <DatePicker
                selected={protocolDateTemp}
                onChange={(date: Date) => setProtocolDateTemp(date)}
                className="input-custom-to-date"
                locale="pt-BR"
                dateFormat="dd/MM/yyyy"
                placeholderText="Selecionar a data"
              />
            </Form.Group>
          </Form.Row>
        </ConfirmationModal>
      </If>

    </div>
  );
}
